import { Alert, AlertDescription, AlertProps, Flex } from '@chakra-ui/react';
import { Info, LucideIcon, TriangleAlert } from 'lucide-react';
import { ReactNode } from 'react';

export type CalloutStatus = 'warning' | 'info';

export interface CalloutProps extends Omit<AlertProps, 'status' | 'children'> {
  /** Drives the color scheme and the default leading icon. */
  status: CalloutStatus;
  /** Optional icon override. Defaults to a sensible icon per status. */
  icon?: ReactNode;
  /** Body content. Wraps cleanly on small screens. */
  children: ReactNode;
}

interface StatusStyle {
  surface: string;
  text: string;
  iconBg: string;
  iconGlyph: string;
  glyphSize: number;
  defaultIcon: LucideIcon;
}

const statusStyles: Record<CalloutStatus, StatusStyle> = {
  warning: {
    surface: 'semantic.warning.surface',
    text: 'semantic.warning.text',
    iconBg: 'semantic.warning.base',
    iconGlyph: 'white',
    glyphSize: 14,
    defaultIcon: TriangleAlert,
  },
  info: {
    surface: 'semantic.info.surface',
    text: 'semantic.info.text',
    iconBg: 'semantic.info.base',
    iconGlyph: 'white',
    glyphSize: 14,
    defaultIcon: Info,
  },
};

/**
 * Reusable callout built on Chakra `<Alert>`.
 *
 * Two variants — `warning` (yellow/orange) and `info` (teal/blue) — driven by
 * the `status` prop. The leading icon sits in a 24px badge at the start of the
 * box, so it aligns right in the app's default RTL direction. Body content
 * passes through as children and wraps cleanly on mobile.
 */
export const Callout: React.FC<CalloutProps> = ({
  status,
  icon,
  children,
  dir = 'rtl',
  ...rest
}) => {
  const {
    surface,
    text,
    iconBg,
    iconGlyph,
    glyphSize,
    defaultIcon: DefaultIcon,
  } = statusStyles[status];

  return (
    <Alert
      dir={dir}
      bg={surface}
      borderRadius='12px'
      alignItems='flex-start'
      gap={3}
      px={4}
      py={3}
      data-testid='callout'
      data-status={status}
      {...rest}
    >
      <Flex
        flexShrink={0}
        boxSize='24px'
        borderRadius='full'
        bg={iconBg}
        color={iconGlyph}
        align='center'
        justify='center'
        data-testid='callout-icon'
      >
        {icon ?? <DefaultIcon size={glyphSize} aria-hidden />}
      </Flex>
      <AlertDescription
        color={text}
        fontFamily="'Thmanyah Sans', 'Noto Sans Arabic', 'Noto Sans', sans-serif"
        fontWeight={400}
        fontSize='12px'
        lineHeight='140%'
        textAlign='right'
        sx={{ overflowWrap: 'anywhere' }}
      >
        {children}
      </AlertDescription>
    </Alert>
  );
};
